// Recording UI: the mic button, timer, countdown and your live voice. Amber means live.
// Audio capture itself lives in the Speak feature; these only draw the state.
import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Icon } from './Icon';

/** Centered column for a recording step. */
export function RecorderStage({ children }: { children: ReactNode }) {
  return <div className="recorder">{children}</div>;
}

/** disabled: shown but not usable yet (a recording shorter than the minimum). */
type MicButtonProps = { live?: boolean; label: string; onClick?: () => void; disabled?: boolean };

/** Big amber button: a mic to start, an ink square to stop while live. */
export function MicButton({ live, label, onClick, disabled }: MicButtonProps) {
  return (
    <button
      className={cx('mic', live && 'live')}
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
    >
      {live ? <span className="stop" /> : <Icon name="mic" />}
    </button>
  );
}

export function Timer({ children }: { children: ReactNode }) {
  return (
    <div className="timer" role="timer" aria-live="off">
      {children}
    </div>
  );
}

/** Ring with the seconds left before recording starts. */
export function Countdown({ seconds }: { seconds: number }) {
  return (
    <div className="countdown num" role="timer" aria-label={`Recording starts in ${seconds} seconds`}>
      {seconds}
    </div>
  );
}

/** Your voice, live: amber bars, newest on the right; older ones fade. levels: 0 to 1. */
export function Levels({ levels }: { levels: readonly number[] }) {
  return (
    <div className="levels" aria-hidden="true">
      {levels.map((l, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional.
        <span key={i} style={{ height: `${Math.round(Math.max(0.05, Math.min(1, l)) * 100)}%` }} />
      ))}
    </div>
  );
}

/** "Recording" with a blinking amber dot. */
export function RecordingLabel() {
  return (
    <span className="rec-label">
      <span className="rec-dot" />
      Recording
    </span>
  );
}
